// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Mutation hook for applying an operator event to an alarm's state machine.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sendAlarmEvent } from '../services/alarmsService';
import { ApiError } from '../services/api';
import { useAlarmWorkOrderStore } from '../stores/alarmWorkOrderStore';
import { stateLabel } from '../types/alarmFsm';
import type { OperatorEvent } from '../types/alarmFsm';

export interface ApplyAlarmEventVars {
  siteId: string;
  /** The per-occurrence identifier. An alarm_code addresses the rule, not a row. */
  alarmId: string;
  event: OperatorEvent;
  /**
   * The event is the alarm step a work-order write is waiting on (the store's
   * `pendingEvent`). Cleared here, in the hook-level callback, so it runs even
   * if the card that sent it unmounted before AEM answered: a mutate-level
   * onSuccess never does, which left the flag behind and showed an alarm
   * update that "did not complete" for an event AEM had already applied.
   */
  settlesPending?: boolean;
}

/** Operator-facing labels for the events this UI can send. */
const EVENT_LABELS: Record<OperatorEvent, string> = {
  ACK: 'acknowledge this alarm',
  SHELVE: 'shelve this alarm',
  UNSHELVE: 'unshelve this alarm',
  WO_CREATE: 'open a work order',
  WO_COMPLETE: 'mark the work order complete',
  WO_REOPEN: 'reopen the work order',
  FORCE_CLEAR: 'force-clear this alarm',
  CLOSE: 'close this alarm',
};

export function isAlarmEventRetryable(error: unknown): boolean {
  if (!(error instanceof ApiError)) return true;
  // 404/409 are a stale view of the alarm's own state; 422 is a validation
  // problem the backend has already rejected once — none of the three would
  // succeed on a bare retry (see alarmEventErrorMessage).
  return error.status !== 404 && error.status !== 409 && error.status !== 422;
}

/**
 * Operator-facing copy for a failed event, keyed off the upstream status.
 *
 * `currentState` is named in the 409 copy because that status is a NORMAL
 * outcome here, not a fault: the machines' operator cells are deliberately
 * partial upstream, so "this action isn't available right now" is the honest
 * reading. The UI derives its buttons from the same delta (types/alarmFsm.ts),
 * so a 409 in practice means the alarm moved underneath the operator.
 */
export function alarmEventErrorMessage(
  error: unknown,
  event: OperatorEvent,
  currentState?: string | null
): string {
  const status = error instanceof ApiError ? error.status : undefined;
  const action = EVENT_LABELS[event];
  switch (status) {
    case 404:
      return `This alarm no longer exists, so Helios could not ${action}.`;
    case 409: {
      const where = stateLabel(currentState);
      return where
        ? `Helios could not ${action}: that isn't available while the alarm is "${where}". It may have changed since this page loaded — refresh to see its current state.`
        : `Helios could not ${action} from the alarm's current state. Refresh to see where it is now.`;
    }
    case 422:
      return 'This action could not be applied because of a problem in Helios. Retrying will not help; report it to an administrator.';
    default:
      return error instanceof Error && error.message ? error.message : `Could not ${action}.`;
  }
}

export function useApplyAlarmEvent() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, ApplyAlarmEventVars>({
    mutationFn: ({ siteId, alarmId, event }) => sendAlarmEvent(siteId, alarmId, event),
    onSuccess: (_data, { siteId, alarmId, settlesPending }) => {
      if (settlesPending) useAlarmWorkOrderStore.getState().setPendingEvent(alarmId, undefined);
      queryClient.invalidateQueries({ queryKey: ['alarm', siteId] });
      queryClient.invalidateQueries({ queryKey: ['alarms', siteId] });
      queryClient.invalidateQueries({ queryKey: ['console-alarms'] });
    },
  });
}
